import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

const BACKEND_PORT = process.env.REACT_APP_BACKEND_PORT || '5000';
const ENV_API_URL = process.env.REACT_APP_API_URL || '';

interface AppConfig {
  backendPort: string;
  frontendPort: string;
  serverHost: string;
  protocol: string;
  apiUrl: string;
  registrationEnabled: boolean;
}

let cachedConfig: AppConfig | null = null;
let configPromise: Promise<AppConfig> | null = null;

const getConfig = async (): Promise<AppConfig> => {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      const currentHost = window.location.hostname;
      const currentPort = window.location.port;
      const protocol = window.location.protocol.replace(':', '');

      const backendHost = currentHost;
      // Use relative path /api, Nginx will proxy to backend
      const backendUrl = ENV_API_URL || '/api';

      const response = await axios.get(`${backendUrl}/auth/config`, {
        timeout: 5000,
      });

      if (response.data && response.data.status === 200) {
        cachedConfig = {
          backendPort: response.data.backendPort || BACKEND_PORT,
          frontendPort: response.data.frontendPort || currentPort,
          serverHost: response.data.serverHost || currentHost,
          protocol: response.data.protocol || protocol,
          apiUrl: response.data.apiUrl || backendUrl,
          registrationEnabled: response.data.registrationEnabled || false,
        };
      } else {
        cachedConfig = {
          backendPort: BACKEND_PORT,
          frontendPort: currentPort,
          serverHost: currentHost,
          protocol: protocol,
          apiUrl: backendUrl,
          registrationEnabled: true,
        };
      }
    } catch (error) {
      const currentHost = window.location.hostname;
      const currentPort = window.location.port;
      const protocol = window.location.protocol.replace(':', '');
      // Use relative path /api as fallback
      const fallbackUrl = ENV_API_URL || '/api';
      cachedConfig = {
        backendPort: BACKEND_PORT,
        frontendPort: currentPort,
        serverHost: currentHost,
        protocol: protocol,
        apiUrl: fallbackUrl,
        registrationEnabled: true,
      };
    }
    return cachedConfig;
  })();

  return configPromise;
};

const getApiBaseUrl = async (): Promise<string> => {
  const config = await getConfig();
  return config.apiUrl;
};

let apiInstance: AxiosInstance | null = null;
let apiInitPromise: Promise<AxiosInstance> | null = null;
let isRefreshingToken = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const onTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const getApi = async (): Promise<AxiosInstance> => {
  if (apiInstance) {
    return apiInstance;
  }

  if (apiInitPromise) {
    return apiInitPromise;
  }

  apiInitPromise = (async () => {
    const baseURL = await getApiBaseUrl();
    apiInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    apiInstance.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    apiInstance.interceptors.response.use(
      async (response: AxiosResponse) => {
        if (response.data && response.data.status === 100) {
          const errorMessage = response.data.error || '请求失败';
          const isLoginAPI = response.config?.url?.includes('/auth/login');
          const isRefreshTokenAPI = response.config?.url?.includes('/auth/refresh-token');

          if ((response.data.sessionExpired || errorMessage.includes('登录已失效')) && !isLoginAPI && !isRefreshTokenAPI) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return Promise.reject(new Error('登录已失效，请重新登录'));
          }

          if ((errorMessage.includes('账号已被禁用') || errorMessage.includes('账户被禁用')) && !isLoginAPI) {
            const storedUser = localStorage.getItem('user');
            if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
              try {
                const user = JSON.parse(storedUser);
                if (user.expires_at) {
                  const now = new Date();
                  const expires = new Date(user.expires_at);
                  const isExpired = now > expires;
                  if (!isExpired) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                    return Promise.reject(new Error('账号已被禁用'));
                  } else {
                    window.dispatchEvent(new CustomEvent('account-disabled-expired'));
                    return Promise.reject(new Error('账号已被禁用'));
                  }
                } else {
                  localStorage.removeItem('token');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  window.location.href = '/login';
                  return Promise.reject(new Error('账号已被禁用'));
                }
              } catch (parseError) {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(new Error('账号已被禁用'));
              }
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              window.location.href = '/login';
              return Promise.reject(new Error('账号已被禁用'));
            }
          }

          const isTokenInvalid = errorMessage.includes('访问令牌无效') ||
                                 errorMessage.includes('访问令牌缺失') ||
                                 errorMessage.includes('访问令牌格式错误');
          if (isTokenInvalid && !isLoginAPI && !isRefreshTokenAPI) {
            const storedRefreshToken = localStorage.getItem('refreshToken');
            if (storedRefreshToken) {
              if (!isRefreshingToken) {
                isRefreshingToken = true;
                try {
                  const api = await getApi();
                  const refreshResponse = await api.post('/auth/refresh-token', { refreshToken: storedRefreshToken });
                  const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data;

                  localStorage.setItem('token', newToken);
                  localStorage.setItem('refreshToken', newRefreshToken);
                  localStorage.setItem('user', JSON.stringify(user));

                  isRefreshingToken = false;
                  onTokenRefreshed(newToken);

                  response.config.headers.Authorization = `Bearer ${newToken}`;
                  return api.request(response.config);
                } catch (refreshError) {
                  isRefreshingToken = false;
                  refreshSubscribers = [];
                  localStorage.removeItem('token');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  window.location.href = '/login';
                  return Promise.reject(new Error('登录已失效，请重新登录'));
                }
              } else {
                return new Promise((resolve) => {
                  addRefreshSubscriber((newToken: string) => {
                    response.config.headers.Authorization = `Bearer ${newToken}`;
                    resolve(getApi().then(api => api.request(response.config)));
                  });
                });
              }
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              window.location.href = '/login';
              return Promise.reject(new Error('登录已失效，请重新登录'));
            }
          }

          const error = new Error(errorMessage) as any;
          error.response = {
            status: 400,
            data: response.data
          };
          return Promise.reject(error);
        }
        return response;
      },
      async (error: AxiosError) => {
        const isAuthAPI = error.config?.url?.includes('/auth/login') ||
                         error.config?.url?.includes('/auth/register') ||
                         error.config?.url?.includes('/auth/refresh-token');

        if (error.response?.status === 401 && !isAuthAPI) {
          const refreshToken = localStorage.getItem('refreshToken');

          if (refreshToken) {
            try {
              const api = await getApi();
              const refreshResponse = await api.post('/auth/refresh-token', { refreshToken });
              const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data;

              localStorage.setItem('token', newToken);
              localStorage.setItem('refreshToken', newRefreshToken);
              localStorage.setItem('user', JSON.stringify(user));

              error.config!.headers!.Authorization = `Bearer ${newToken}`;
              return api.request(error.config!);
            } catch (refreshError) {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              window.location.href = '/login';
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );

    return apiInstance;
  })();

  return apiInitPromise;
};

const getExternalApiUrl = async (): Promise<string> => {
  const config = await getConfig();
  return config.apiUrl;
};

const renewalAPI = {
  checkPaymentAvailable: async () => {
    const api = await getApi();
    return api.get('/renewal/check-payment-available');
  },
  getRenewalConfig: async () => {
    const api = await getApi();
    return api.get('/renewal/config');
  },
  createRenewalOrder: async (data: { paymentMethod: 'wechat' | 'alipay' | 'balance'; renewalUnit: string; renewalQuantity: number }) => {
    const api = await getApi();
    return api.post('/renewal/order', data);
  },
  getRenewalOrderStatus: async (orderId: string) => {
    const api = await getApi();
    return api.get(`/renewal/order/${orderId}/status`);
  },
};

const authAPI = {
  register: async (data: { username: string; email: string; password: string; invite_code?: string }) => {
    const api = await getApi();
    return api.post('/auth/register', data);
  },

  login: async (data: { username: string; password: string }) => {
    const api = await getApi();
    return api.post('/auth/login', data);
  },

  getPublicKey: async () => {
    const api = await getApi();
    return api.get('/auth/public-key');
  },

  getCaptcha: async () => {
    const api = await getApi();
    return api.get('/auth/captcha');
  },

  getSecuritySettings: async () => {
    const api = await getApi();
    return api.get('/auth/security-settings');
  },

  refreshToken: async (data: { refreshToken: string }) => {
    const api = await getApi();
    return api.post('/auth/refresh-token', data);
  },

  getProfile: async () => {
    const api = await getApi();
    return api.get('/auth/profile');
  },

  getMenuPermissions: async () => {
    const api = await getApi();
    return api.get('/auth/menu-permissions');
  },

  getApiDocs: async () => {
    const api = await getApi();
    return api.get('/auth/api-docs');
  },

  getApiChargeList: async () => {
    const api = await getApi();
    return api.get('/auth/api-charge-list');
  },

  getApiPricingForUser: async () => {
    const api = await getApi();
    return api.get('/auth/api-pricing');
  },

  refreshApiToken: async () => {
    const api = await getApi();
    return api.post('/auth/refresh-api-token');
  },

  getApiKeys: async () => {
    const api = await getApi();
    return api.get('/auth/api-keys');
  },

  createApiKey: async (data: { name: string; category_id?: number }) => {
    const api = await getApi();
    return api.post('/auth/api-keys', data);
  },

  deleteApiKey: async (id: number) => {
    const api = await getApi();
    return api.delete(`/auth/api-keys/${id}`);
  },

  batchDeleteApiKeys: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/auth/api-keys/batch', { data: { ids } });
  },

  updateApiKey: async (id: number, data: { name: string; category_id: number }) => {
    const api = await getApi();
    return api.put(`/auth/api-keys/${id}`, data);
  },

  getConfig: async () => {
    const api = await getApi();
    return api.get('/auth/config');
  },

  getCategories: async () => {
    const api = await getApi();
    return api.get('/auth/categories');
  },

  getCheckinStatus: async () => {
    const api = await getApi();
    return api.get('/auth/checkin/status');
  },

  performCheckin: async () => {
    const api = await getApi();
    return api.post('/auth/checkin');
  },

  verifyAdmin: async () => {
    const api = await getApi();
    return api.get('/auth/admin/verify');
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const api = await getApi();
    return api.put('/auth/change-password', data);
  },

  updateProfile: async (data: FormData) => {
    const api = await getApi();
    return api.put('/auth/update-profile', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  changeUserPassword: async (data: {
    userId: number;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const api = await getApi();
    return api.put('/auth/admin/change-user-password', data);
  },

  getRenewalConfig: async (productType?: 'membership' | 'card' | 'order_assistant') => {
    const api = await getApi();
    return api.get('/renewal/config', { params: productType ? { productType } : undefined });
  },
  createRenewalOrder: async (data: { paymentMethod: 'wechat' | 'alipay' | 'balance'; productId: number; quantity: number }) => {
    const api = await getApi();
    return api.post('/renewal/order', data);
  },
  getRenewalOrderStatus: async (orderId: string) => {
    const api = await getApi();
    return api.get(`/renewal/order/${orderId}/status`);
  },
};

const cardBindAPI = {
  getBindInfo: async () => {
    const api = await getApi();
    return api.get('/card/bind/info');
  },
  bindCard: async (cardKey: string) => {
    const api = await getApi();
    return api.post('/card/bind', { cardKey });
  },
  unbindCard: async () => {
    const api = await getApi();
    return api.post('/card/unbind');
  },
};

const dataAPI = {
  uploadFile: async (file: File, categoryId?: number) => {
    const api = await getApi();
    const formData = new FormData();
    formData.append('file', file);
    if (categoryId) {
      formData.append('category_id', categoryId.toString());
    }
    return api.post('/data/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getData: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    notes_search?: string;
    category_id?: number | string;
    empty_notes?: string;
    created_from?: string;
    created_to?: string;
    sort_by?: string;
    sort_order?: string;
  }) => {
    const api = await getApi();
    return api.get('/data', { params });
  },

  addData: async (data: { content: string; status: string; notes?: string; category_id?: number }) => {
    const api = await getApi();
    return api.post('/data', data);
  },

  batchAddData: async (dataLines: string[], status: string, notes?: string, categoryId?: number) => {
    const api = await getApi();
    return api.post('/data/batch-add', { dataLines, status, notes, category_id: categoryId });
  },

  updateData: async (id: number, data: { status?: string; notes?: string }) => {
    const api = await getApi();
    return api.get(`/data/${id}/update`, { params: data });
  },

  deleteData: async (id: number) => {
    const api = await getApi();
    return api.delete(`/data/${id}`);
  },

  batchDeleteData: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/data/batch', { data: { ids } });
  },

  batchUpdateData: async (ids: number[], data: { status: string; notes?: string }) => {
    const api = await getApi();
    return api.get('/data/batch/update', { params: { ids: JSON.stringify(ids), ...data } });
  },

  deleteAllData: async (category_id?: number) => {
    const api = await getApi();
    return api.delete('/data/all', { params: category_id ? { category_id } : {} });
  },

  deleteDataByStatus: async (status: string, category_id?: number) => {
    const api = await getApi();
    return api.delete('/data/by-status', { params: { status, ...(category_id ? { category_id } : {}) } });
  },

  getStats: async (category_id?: number) => {
    const api = await getApi();
    return api.get('/data/stats', { params: category_id ? { category_id } : {} });
  },

  batchSetPending: async (category_id?: number) => {
    const api = await getApi();
    return api.post('/data/batch-set-pending', category_id ? { category_id } : {});
  },

  exportData: async (params: { category_id?: number | string; status?: string; ids?: string }) => {
    const api = await getApi();
    return api.get('/data/export', { params });
  },

  changeStatus: async (data: {
    from_status: string | string[];
    to_status: string;
    category_id?: number;
    status?: string;
    empty_notes?: string;
    content_search?: string;
    notes_search?: string;
  }) => {
    const api = await getApi();
    return api.put('/data/change-status', data);
  },

  deleteFilteredData: async (params: {
    status?: string;
    category_id?: string;
    search?: string;
    notes_search?: string;
    empty_notes?: string;
  }) => {
    const api = await getApi();
    return api.delete('/data/filtered', { params });
  },

  deleteDuplicateData: async (category_id?: number) => {
    const api = await getApi();
    return api.delete('/data/duplicates', { params: category_id ? { category_id } : {} });
  },

  fetchUnused: async (categoryId: number) => {
    const api = await getApi();
    return api.post('/data/fetch-unused', { category_id: categoryId });
  },

  fetchUnusedPricing: async () => {
    const api = await getApi();
    return api.get('/data/fetch-unused-pricing');
  },
};

const categoryAPI = {
  getCategories: async () => {
    const api = await getApi();
    return api.get('/categories');
  },

  createCategory: async (data: { name: string; description?: string }) => {
    const api = await getApi();
    return api.post('/categories', data);
  },

  updateCategory: async (id: number, data: { name: string; description?: string }) => {
    const api = await getApi();
    return api.put(`/categories/${id}`, data);
  },

  deleteCategory: async (id: number) => {
    const api = await getApi();
    return api.delete(`/categories/${id}`);
  },

  getDefaultCategory: async () => {
    const api = await getApi();
    return api.get('/categories/default');
  },
};

const clusterAPI = {
  getNodes: async () => {
    const api = await getApi();
    return api.get('/admin/cluster/nodes');
  },

  getNode: async (id: number) => {
    const api = await getApi();
    return api.get(`/admin/cluster/nodes/${id}`);
  },

  createNode: async (data: {
    name: string;
    host: string;
    port: number;
    protocol?: 'http' | 'https';
    supports_read: boolean;
    supports_write: boolean;
    is_enabled?: boolean;
    enable_forward?: boolean;
    priority?: number;
    description?: string;
  }) => {
    const api = await getApi();
    return api.post('/admin/cluster/nodes', data);
  },

  updateNode: async (id: number, data: {
    name?: string;
    host?: string;
    port?: number;
    protocol?: 'http' | 'https';
    supports_read?: boolean;
    supports_write?: boolean;
    is_enabled?: boolean;
    enable_forward?: boolean;
    priority?: number;
    description?: string;
  }) => {
    const api = await getApi();
    return api.put(`/admin/cluster/nodes/${id}`, data);
  },

  deleteNode: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/cluster/nodes/${id}`);
  },

  testNode: async (id: number) => {
    const api = await getApi();
    return api.post(`/admin/cluster/nodes/${id}/test`);
  },
};

const externalAPI = {
  getData: async (params: { api_key: string; limit?: number; status?: string }) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/data`, { params });
  },

  updateData: async (id: number, data: {
    status?: string;
    notes?: string;
    response_data?: any;
  }, apiKey: string) => {
    const baseUrl = await getExternalApiUrl();
    const params = new URLSearchParams();
    params.append('api_key', apiKey);
    if (data.status) params.append('status', data.status);
    if (data.notes) params.append('notes', data.notes);
    if (data.response_data) params.append('response_data', JSON.stringify(data.response_data));
    return axios.get(`${baseUrl}/external/data/${id}/update?${params.toString()}`);
  },

  batchUpdateData: async (data: {
    ids: number[];
    status: string;
    notes?: string;
  }, apiKey: string) => {
    const baseUrl = await getExternalApiUrl();
    const params = new URLSearchParams();
    params.append('api_key', apiKey);
    params.append('ids', JSON.stringify(data.ids));
    params.append('status', data.status);
    if (data.notes) params.append('notes', data.notes);
    return axios.get(`${baseUrl}/external/data/batch/update?${params.toString()}`);
  },

  getStats: async (apiKey: string) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/stats`, { params: { api_key: apiKey } });
  },

  getUsage: async (apiKey: string, params?: { page?: number; limit?: number }) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/usage`, { params: { ...params, api_key: apiKey } });
  },

  reportOnline: async (apiKey: string) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/data/online`, { params: { api_key: apiKey } });
  },

  getClusterNode: async (apiKey: string, params?: { type?: string; enable_forward?: string }) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/data/cluster/node`, { params: { ...params, api_key: apiKey } });
  },

  getWxData: async (apiKey: string, url: string) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/data/wxdata`, { params: { api_key: apiKey, url } });
  },

  getWxPending: async (apiKey: string) => {
    const baseUrl = await getExternalApiUrl();
    return axios.get(`${baseUrl}/external/data/wxpending`, { params: { api_key: apiKey } });
  },

  getWxStatus: async (apiKey: string, id: number, status: string, notes?: string) => {
    const baseUrl = await getExternalApiUrl();
    const params: Record<string, string> = { api_key: apiKey, ID: String(id), status };
    if (notes) params.notes = notes;
    return axios.get(`${baseUrl}/external/data/wxstatus`, { params });
  },
};

const adminAPI = {
  getSecuritySettings: async () => {
    const api = await getApi();
    return api.get('/admin/security/settings');
  },
  updateSecuritySettings: async (settings: any) => {
    const api = await getApi();
    return api.put('/admin/security/settings', settings);
  },

  getUsers: async (params?: { search?: string; page?: number; limit?: number; role?: string }) => {
    const api = await getApi();
    return api.get('/admin/users', { params });
  },
  updateUserStatus: async (userId: number, isActive: boolean) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/status`, { is_active: isActive });
  },
  updateUserAdmin: async (userId: number, isAdmin: boolean) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/admin`, { is_admin: isAdmin });
  },
  updateUserWechatAuth: async (userId: number, isWechatAuthEnabled: boolean) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/wechat-auth`, { is_wechat_auth_enabled: isWechatAuthEnabled });
  },
  updateUserRole: async (userId: number, role: string, isWechatAuthEnabled?: boolean, customMenuPermissions?: string[]) => {
    const api = await getApi();
    const data: any = { role };
    if (isWechatAuthEnabled !== undefined) {
      data.is_wechat_auth_enabled = isWechatAuthEnabled;
    }
    if (customMenuPermissions !== undefined) {
      data.custom_menu_permissions = customMenuPermissions;
    }
    return api.put(`/admin/users/${userId}/role`, data);
  },
  updateUserExpires: async (userId: number, expiresAt: string | null, yearlyVipExpiresAt?: string | null) => {
    const api = await getApi();
    const data: any = { expires_at: expiresAt };
    if (yearlyVipExpiresAt !== undefined) {
      data.yearly_vip_expires_at = yearlyVipExpiresAt;
    }
    return api.put(`/admin/users/${userId}/expires`, data);
  },
  renewUser: async (userId: number) => {
    const api = await getApi();
    return api.post(`/admin/users/${userId}/renew`);
  },
  deleteUser: async (userId: number) => {
    const api = await getApi();
    return api.delete(`/admin/users/${userId}`);
  },
  changeUserPassword: async (data: {
    userId: number;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const api = await getApi();
    return api.put('/auth/admin/change-user-password', data);
  },

  batchUpdateUserStatus: async (userIds: number[], isActive: boolean) => {
    const api = await getApi();
    return api.put('/admin/users/batch/status', { user_ids: userIds, is_active: isActive });
  },
  batchUpdateUserExpires: async (userIds: number[], expiresAt: string | null) => {
    const api = await getApi();
    return api.put('/admin/users/batch/expires', { user_ids: userIds, expires_at: expiresAt });
  },
  batchUpdateUserAdmin: async (userIds: number[], isAdmin: boolean) => {
    const api = await getApi();
    return api.put('/admin/users/batch/admin', { user_ids: userIds, is_admin: isAdmin });
  },

  generateInviteCode: async () => {
    const api = await getApi();
    return api.post('/admin/invite-codes');
  },
  getInviteCodes: async () => {
    const api = await getApi();
    return api.get('/admin/invite-codes');
  },
  deleteInviteCode: async (codeId: number) => {
    const api = await getApi();
    return api.delete(`/admin/invite-codes/${codeId}`);
  },

  getStats: async () => {
    const api = await getApi();
    return api.get('/admin/stats');
  },

  getLoginLogs: async (params?: { page?: number; limit?: number; search?: string; login_type?: string }) => {
    const api = await getApi();
    return api.get('/admin/login-logs', { params });
  },

  getApiUsageLogs: async (params?: { page?: number; limit?: number; search?: string; endpoint?: string; method?: string }) => {
    const api = await getApi();
    return api.get('/admin/api-usage-logs', { params });
  },

  getServerInfo: async () => {
    const api = await getApi();
    return api.get('/admin/server-info');
  },

  getFinancialSummary: async () => {
    const api = await getApi();
    return api.get('/admin/financial-summary');
  },

  getCleanupStatus: async () => {
    const api = await getApi();
    return api.get('/admin/cleanup-status');
  },

  getRenewalSettings: async () => {
    const api = await getApi();
    return api.get('/admin/renewal/settings');
  },
  updateRenewalSettings: async (settings: any) => {
    const api = await getApi();
    return api.put('/admin/renewal/settings', settings);
  },
  getRenewalOrders: async (params?: { page?: number; limit?: number; status?: string }) => {
    const api = await getApi();
    return api.get('/admin/renewal/orders', { params });
  },
  getRenewalProducts: async (productType?: 'membership' | 'card' | 'order_assistant') => {
    const api = await getApi();
    return api.get('/admin/renewal/products', { params: productType ? { productType } : undefined });
  },
  createRenewalProduct: async (product: { productName: string; price: number; discountRate?: number; rebateAmount?: number; rebatePoints?: number; renewalUnit?: string; renewalQuantity?: number; productType?: string; isDefault?: boolean; sortOrder?: number }) => {
    const api = await getApi();
    return api.post('/admin/renewal/products', product);
  },
  updateRenewalProduct: async (id: number, product: { productName?: string; price?: number; discountRate?: number; rebateAmount?: number; rebatePoints?: number; renewalUnit?: string; renewalQuantity?: number; productType?: string; isDefault?: boolean; sortOrder?: number }) => {
    const api = await getApi();
    return api.put(`/admin/renewal/products/${id}`, product);
  },
  deleteRenewalProduct: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/renewal/products/${id}`);
  },
  setCleanupTime: async (time: string) => {
    const api = await getApi();
    return api.post('/admin/cleanup-time', { time });
  },
  triggerCleanup: async () => {
    const api = await getApi();
    return api.post('/admin/cleanup-now');
  },

  getLogCleanupConfig: async () => {
    const api = await getApi();
    return api.get('/admin/log-cleanup-config');
  },
  setLogCleanupConfig: async (config: any) => {
    const api = await getApi();
    return api.post('/admin/log-cleanup-config', config);
  },
  getLogFiles: async () => {
    const api = await getApi();
    return api.get('/admin/log-files');
  },
  triggerLogCleanup: async () => {
    const api = await getApi();
    return api.post('/admin/log-cleanup-now');
  },

  getApiLoggingEnabled: async () => {
    const api = await getApi();
    return api.get('/admin/api-logging-enabled');
  },
  setApiLoggingEnabled: async (enabled: boolean) => {
    const api = await getApi();
    return api.post('/admin/api-logging-enabled', { enabled });
  },

  getDataApiLoggingEnabled: async () => {
    const api = await getApi();
    return api.get('/admin/data-api-logging-enabled');
  },
  setDataApiLoggingEnabled: async (enabled: boolean) => {
    const api = await getApi();
    return api.post('/admin/data-api-logging-enabled', { enabled });
  },

  getCardApiLoggingEnabled: async () => {
    const api = await getApi();
    return api.get('/admin/card-api-logging-enabled');
  },
  setCardApiLoggingEnabled: async (enabled: boolean) => {
    const api = await getApi();
    return api.post('/admin/card-api-logging-enabled', { enabled });
  },

  getDeviceOfflineInterval: async () => {
    const api = await getApi();
    return api.get('/admin/device-offline-interval');
  },
  setDeviceOfflineInterval: async (interval: number) => {
    const api = await getApi();
    return api.post('/admin/device-offline-interval', { interval });
  },

  getCardOnlineTimeout: async () => {
    const api = await getApi();
    return api.get('/admin/card-online-timeout');
  },
  setCardOnlineTimeout: async (intervalMs: number) => {
    const api = await getApi();
    return api.post('/admin/card-online-timeout', { interval_ms: intervalMs });
  },

  getRequestSkew: async () => {
    const api = await getApi();
    return api.get('/admin/request-skew');
  },
  setRequestSkew: async (skewMs: number) => {
    const api = await getApi();
    return api.post('/admin/request-skew', { skew_ms: skewMs });
  },

  getDeviceVerificationEnabled: async () => {
    const api = await getApi();
    return api.get('/admin/device-verification-enabled');
  },
  setDeviceVerificationEnabled: async (enabled: boolean) => {
    const api = await getApi();
    return api.post('/admin/device-verification-enabled', { enabled });
  },
  getDefaultMaxOnlineDevices: async () => {
    const api = await getApi();
    return api.get('/admin/default-max-online-devices');
  },
  setDefaultMaxOnlineDevices: async (max_online_devices: number) => {
    const api = await getApi();
    return api.post('/admin/default-max-online-devices', { max_online_devices });
  },

  getApiLogStats: async () => {
    const api = await getApi();
    return api.get('/admin/api-log-stats');
  },
  updateLogCleanupConfig: async (data: { retentionDays: number; autoCleanup: boolean; cleanupHour: number }) => {
    const api = await getApi();
    return api.post('/admin/log-cleanup-config', data);
  },
  runCleanupNow: async () => {
    const api = await getApi();
    return api.post('/admin/cleanup-now');
  },

  getDataLogCleanupConfig: async () => {
    const api = await getApi();
    return api.get('/admin/data-log-cleanup-config');
  },
  updateDataLogCleanupConfig: async (data: { enabled: boolean; retention_days: number; cleanup_time: string }) => {
    const api = await getApi();
    return api.post('/admin/data-log-cleanup-config', data);
  },
  runDataLogCleanup: async () => {
    const api = await getApi();
    return api.post('/admin/data-log-cleanup-now');
  },

  getCardLogCleanupConfig: async () => {
    const api = await getApi();
    return api.get('/admin/card-log-cleanup-config');
  },
  updateCardLogCleanupConfig: async (data: { enabled: boolean; retention_days: number; cleanup_time: string }) => {
    const api = await getApi();
    return api.post('/admin/card-log-cleanup-config', data);
  },
  runCardLogCleanup: async () => {
    const api = await getApi();
    return api.post('/admin/card-log-cleanup-now');
  },

  getCheckinSettings: async () => {
    const api = await getApi();
    return api.get('/admin/checkin/settings');
  },
  updateCheckinSettings: async (settings: any) => {
    const api = await getApi();
    return api.post('/admin/checkin/settings', settings);
  },

  getUserOnlineDevices: async (userId: number) => {
    const api = await getApi();
    return api.get(`/admin/users/${userId}/online-devices`);
  },
  updateUserMaxOnlineDevices: async (userId: number, maxOnlineDevices: number) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/max-online-devices`, { max_online_devices: maxOnlineDevices });
  },

  updateUserBalance: async (userId: number, balance: number, reason?: string) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/balance`, { balance, reason });
  },

  updateUserExclusiveBalance: async (userId: number, exclusiveBalance: number, reason?: string) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/exclusive-balance`, { exclusiveBalance, reason });
  },

  updateUserBoundCard: async (userId: number, cardKey: string) => {
    const api = await getApi();
    return api.put(`/admin/users/${userId}/bind-card`, { cardKey });
  },

  getUserBalanceLogs: async (userId: number, params?: { page?: number; limit?: number; direction?: string; type?: string; description?: string }) => {
    const api = await getApi();
    return api.get(`/admin/users/${userId}/logs`, { params });
  },

  getWechatAuthCleanupStatus: async () => {
    const api = await getApi();
    return api.get('/admin/wechat-auth-cleanup-status');
  },
  setWechatAuthCleanupConfig: async (config: { enabled?: boolean; cleanupTime?: string; retentionDays?: number }) => {
    const api = await getApi();
    return api.post('/admin/wechat-auth-cleanup-config', config);
  },
  triggerWechatAuthCleanup: async () => {
    const api = await getApi();
    return api.post('/admin/wechat-auth-cleanup-now');
  },

  getUserCheckins: async (userId: number, params?: { page?: number; limit?: number }) => {
    const api = await getApi();
    return api.get(`/admin/users/${userId}/checkins`, { params });
  },

  getAnnouncements: async () => {
    const api = await getApi();
    return api.get('/admin/announcements');
  },
  getActiveAnnouncements: async () => {
    const api = await getApi();
    return api.get('/admin/announcements/active');
  },
  createAnnouncement: async (data: { title: string; content: string; start_time: string; end_time: string }) => {
    const api = await getApi();
    return api.post('/admin/announcements', data);
  },
  updateAnnouncement: async (id: number, data: { title: string; content: string; start_time: string; end_time: string }) => {
    const api = await getApi();
    return api.put(`/admin/announcements/${id}`, data);
  },
  updateAnnouncementStatus: async (id: number, isEnabled: boolean) => {
    const api = await getApi();
    return api.put(`/admin/announcements/${id}/status`, { is_enabled: isEnabled });
  },
  deleteAnnouncement: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/announcements/${id}`);
  },

  getHomepageConfig: async (key: string) => {
    const api = await getApi();
    return api.get(`/admin/homepage-config/${key}`);
  },
  updateHomepageConfig: async (key: string, value: string) => {
    const api = await getApi();
    return api.put(`/admin/homepage-config/${key}`, { value });
  },

  getGameBlockList: async () => {
    const api = await getApi();
    return api.get('/admin/game-block-list');
  },
  createGameBlockItem: async (data: { game_name: string; game_id: string }) => {
    const api = await getApi();
    return api.post('/admin/game-block-list', data);
  },
  updateGameBlockItem: async (id: number, data: { game_name: string; game_id: string }) => {
    const api = await getApi();
    return api.put(`/admin/game-block-list/${id}`, data);
  },
  deleteGameBlockItem: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/game-block-list/${id}`);
  },
  updateGameBlockItemStatus: async (id: number, isEnabled: boolean) => {
    const api = await getApi();
    return api.put(`/admin/game-block-list/${id}/status`, { is_enabled: isEnabled });
  },

  getXiaomiBindDataList: async (params?: { search?: string; page?: number; limit?: number; status?: number; pt?: string }) => {
    const api = await getApi();
    return api.get('/admin/xiaomi-bind-data', { params });
  },
  deleteXiaomiBindData: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/xiaomi-bind-data/${id}`);
  },
  batchDeleteXiaomiBindData: async (ids: number[]) => {
    const api = await getApi();
    return api.post('/admin/xiaomi-bind-data/batch-delete', { ids });
  },

  getApiPricingList: async () => {
    const api = await getApi();
    return api.get('/admin/api-pricing');
  },
  createApiPricing: async (data: { api_path: string; api_name: string; price_normal: number; price_monthly: number; price_yearly: number; price_permanent: number; free_users?: string }) => {
    const api = await getApi();
    return api.post('/admin/api-pricing', data);
  },
  updateApiPricing: async (id: number, data: { api_name: string; price_normal: number; price_monthly: number; price_yearly: number; price_permanent: number; free_users?: string; is_enabled?: boolean; log_enabled?: boolean; is_visible?: boolean }) => {
    const api = await getApi();
    return api.put(`/admin/api-pricing/${id}`, data);
  },
  deleteApiPricing: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/api-pricing/${id}`);
  },

  getTranscodeConfigList: async () => {
    const api = await getApi();
    return api.get('/admin/transcode-config');
  },
  createTranscodeConfig: async (data: { token: string; cookie: string; remark: string; auto_update_ck: boolean; update_interval: number }) => {
    const api = await getApi();
    return api.post('/admin/transcode-config', data);
  },
  updateTranscodeConfig: async (id: number, data: { token: string; cookie: string; remark: string; auto_update_ck: boolean; update_interval: number }) => {
    const api = await getApi();
    return api.put(`/admin/transcode-config/${id}`, data);
  },
  updateTranscodeConfigStatus: async (id: number, isEnabled: boolean) => {
    const api = await getApi();
    return api.put(`/admin/transcode-config/${id}/status`, { is_enabled: isEnabled });
  },
  refreshTranscodeConfigCK: async (id: number) => {
    const api = await getApi();
    return api.post(`/admin/transcode-config/${id}/refresh-ck`);
  },
  deleteTranscodeConfig: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/transcode-config/${id}`);
  },
  scanLoginStart: async () => {
    const api = await getApi();
    return api.post('/admin/transcode-config/scan-login/start');
  },
  scanLoginPoll: async (sessionId: string) => {
    const api = await getApi();
    return api.get(`/admin/transcode-config/scan-login/poll?session_id=${sessionId}`);
  },
  scanLoginComplete: async (sessionId: string) => {
    const api = await getApi();
    return api.post('/admin/transcode-config/scan-login/complete', { session_id: sessionId });
  },
  getAesConfig: async () => {
    const api = await getApi();
    return api.get('/admin/aes-config');
  },
  updateAesConfig: async (data: { aes_key: string; aes_iv: string; platform_aes_key: string; platform_aes_iv: string }) => {
    const api = await getApi();
    return api.put('/admin/aes-config', data);
  },
  getRsaConfig: async () => {
    const api = await getApi();
    return api.get('/admin/rsa-config');
  },
  generateRsaKeyPair: async () => {
    const api = await getApi();
    return api.post('/admin/rsa-config/generate');
  },
  updateRsaConfig: async (data: { rsa_public_key: string; rsa_private_key: string }) => {
    const api = await getApi();
    return api.put('/admin/rsa-config', data);
  },
};

const kmInterfaceAPI = {
  list: async (params: { search?: string } = {}) => {
    const api = await getApi();
    return api.get('/admin/km-interfaces', { params });
  },
  create: async (data: {
    api_path: string;
    api_name: string;
    method: string;
    request_params?: string;
    request_desc?: string;
    response_example?: string;
    description?: string;
    is_enabled?: boolean;
    log_enabled?: boolean;
    dedup_enabled?: boolean;
    is_visible?: boolean;
    sort_order?: number;
  }) => {
    const api = await getApi();
    return api.post('/admin/km-interfaces', data);
  },
  update: async (id: number, data: {
    api_path: string;
    api_name: string;
    method: string;
    request_params?: string;
    request_desc?: string;
    response_example?: string;
    description?: string;
    is_enabled?: boolean;
    log_enabled?: boolean;
    dedup_enabled?: boolean;
    is_visible?: boolean;
    sort_order?: number;
  }) => {
    const api = await getApi();
    return api.put(`/admin/km-interfaces/${id}`, data);
  },
  updateStatus: async (id: number, isEnabled: boolean) => {
    const api = await getApi();
    return api.put(`/admin/km-interfaces/${id}/status`, { is_enabled: isEnabled });
  },
  remove: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/km-interfaces/${id}`);
  },
};

const rechargeAPI = {
  getConfig: async () => {
    const api = await getApi();
    return api.get('/recharge/config');
  },
  createOrder: async (data: { amount: number; paymentMethod: 'wechat' | 'alipay' }) => {
    const api = await getApi();
    return api.post('/recharge/order', data);
  },

  getOrderStatus: async (orderId: string) => {
    const api = await getApi();
    return api.get(`/recharge/order/${orderId}/status`);
  },

  getBalance: async () => {
    const api = await getApi();
    return api.get('/recharge/balance');
  },
};

const inviteAPI = {
  purchaseCode: async (data: { productId: number; quantity: number; paymentMethod: 'wechat' | 'alipay' | 'balance' }) => {
    const api = await getApi();
    return api.post('/invite/purchase', data);
  },

  getPrice: async () => {
    const api = await getApi();
    return api.get('/invite/price');
  },

  getMyCodes: async () => {
    const api = await getApi();
    return api.get('/invite/my-codes');
  },

  deleteMyCode: async (codeId: number) => {
    const api = await getApi();
    return api.delete(`/invite/my-codes/${codeId}`);
  },

  getPurchaseOrderStatus: async (orderId: string) => {
    const api = await getApi();
    return api.get(`/invite/purchase/order/${orderId}/status`);
  },

  getRebateStats: async () => {
    const api = await getApi();
    return api.get('/invite/rebate-stats');
  },
};

const balanceAPI = {
  getHistory: async (page?: number, limit?: number) => {
    const api = await getApi();
    return api.get('/balance/history', { params: { page, limit } });
  },
};

const withdrawAPI = {
  apply: async (data: { amount: number; receiptImage: string; note?: string }) => {
    const api = await getApi();
    return api.post('/withdraw/apply', data);
  },
  getMyRequests: async () => {
    const api = await getApi();
    return api.get('/withdraw/my-requests');
  },
  getAdminRequests: async (params?: { status?: string; page?: number; pageSize?: number; search?: string }) => {
    const api = await getApi();
    return api.get('/withdraw/admin/requests', { params });
  },
  approveRequest: async (id: number, data?: { adminNote?: string }) => {
    const api = await getApi();
    return api.post(`/withdraw/admin/requests/${id}/approve`, data);
  },
  rejectRequest: async (id: number, data?: { adminNote?: string }) => {
    const api = await getApi();
    return api.post(`/withdraw/admin/requests/${id}/reject`, data);
  },
};

const wechatAuthAPI = {
  getList: async (params?: { page?: number; limit?: number; auth_status?: string; search?: string }) => {
    const api = await getApi();
    return api.get('/wechat-auth/list', { params });
  },
  delete: async (id: number) => {
    const api = await getApi();
    return api.delete(`/wechat-auth/${id}`);
  },
  batchDelete: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/wechat-auth/batch', { data: { ids } });
  },
  update: async (id: number, data: { secret_key?: string; qr_code_url?: string; auth_result?: string }) => {
    const api = await getApi();
    return api.put(`/wechat-auth/${id}`, data);
  },
};

const cardAPI = {
  getCards: async (params?: {
    page?: number;
    limit?: number;
    qq?: string;
    machine_code?: string;
    card_key?: string;
    is_disabled?: string;
    is_online?: string;
    card_type?: string;
    expire_time_filter?: string;
    expire_time_operator?: string;
  }) => {
    const api = await getApi();
    return api.get('/cards', { params });
  },
  createCard: async (data: { card_key?: string; qq?: string; card_type?: string; rebind_count?: number }) => {
    const api = await getApi();
    return api.post('/cards', data);
  },
  batchCreateCards: async (data: { count: number; qq?: string; card_type?: string; rebind_count?: number }) => {
    const api = await getApi();
    return api.post('/cards/batch', data);
  },
  updateCard: async (id: number, data: Record<string, any>) => {
    const api = await getApi();
    return api.put(`/cards/${id}`, data);
  },
  batchUpdateCards: async (ids: number[], fields: Record<string, any>) => {
    const api = await getApi();
    return api.put('/cards/batch', { ids, fields });
  },
  updateAllCards: async (fields: Record<string, any>) => {
    const api = await getApi();
    return api.put('/cards/all', { fields });
  },
  batchUpdateAllCards: async (fields: Record<string, any>, filters?: Record<string, any>) => {
    const api = await getApi();
    return api.put('/cards/batch/all', { fields, filters });
  },
  deleteCard: async (id: number) => {
    const api = await getApi();
    return api.delete(`/cards/${id}`);
  },
  batchDeleteCards: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/cards/batch', { data: { ids } });
  },
  batchDeleteCardsPost: async (ids: number[]) => {
    const api = await getApi();
    return api.post('/cards/batch/delete', { ids });
  },
  renewCard: async (id: number) => {
    const api = await getApi();
    return api.post(`/cards/${id}/renew`);
  },
  batchRenewCards: async (ids: number[]) => {
    const api = await getApi();
    return api.post('/cards/batch/renew', { ids });
  },
};

const dictionaryAPI = {
  getDictionaries: async () => {
    const api = await getApi();
    return api.get('/dictionaries');
  },
  getDictionariesByKey: async (dictKey: string) => {
    const api = await getApi();
    return api.get(`/dictionaries/key/${dictKey}`);
  },
  getDictKeys: async () => {
    const api = await getApi();
    return api.get('/dictionaries/keys/list');
  },
  createDictionary: async (data: {
    bind_key: string;
    dict_key: string;
    dict_value: string;
    return_value: string;
    color?: string;
    icon?: string;
    sort_order?: number;
    is_default?: boolean;
    duration?: number;
  }) => {
    const api = await getApi();
    return api.post('/dictionaries', data);
  },
  updateDictionary: async (id: number, data: {
    bind_key: string;
    dict_key: string;
    dict_value: string;
    return_value: string;
    color?: string;
    icon?: string;
    sort_order?: number;
    is_default?: boolean;
    duration?: number;
  }) => {
    const api = await getApi();
    return api.put(`/dictionaries/${id}`, data);
  },
  deleteDictionary: async (id: number) => {
    const api = await getApi();
    return api.delete(`/dictionaries/${id}`);
  },
};

const recordLogAPI = {
  getPrizeLogs: async (params?: {
    page?: number;
    limit?: number;
    card_key?: string;
    qq?: string;
    search?: string;
    exclude_keyword?: string;
  }) => {
    const api = await getApi();
    return api.get('/record-logs/prize', { params });
  },
  createPrizeLog: async (data: {
    card_key: string;
    qq?: string;
    software_name?: string;
    activity_name?: string;
    content?: string;
  }) => {
    const api = await getApi();
    return api.post('/record-logs/prize', data);
  },
  batchDeletePrizeLogs: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/record-logs/prize/batch', { data: { ids } });
  },
  getApiLogs: async (params?: {
    page?: number;
    limit?: number;
    card_key?: string;
    qq?: string;
    operation_type?: string;
    exclude_keyword?: string;
  }) => {
    const api = await getApi();
    return api.get('/record-logs/api', { params });
  },
  getCardApiLogs: async (cardKey: string) => {
    const api = await getApi();
    return api.get(`/record-logs/api/card/${cardKey}`);
  },
  batchDeleteApiLogs: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/record-logs/api/batch', { data: { ids } });
  },

  getPrizeLogStats: async (params?: {
    card_key?: string;
    qq?: string;
    software_name?: string;
    activity_name?: string;
    search?: string;
    exclude_keyword?: string;
  }) => {
    const api = await getApi();
    return api.get('/record-logs/prize/stats', { params });
  },

  getApiLogStats: async (params?: {
    card_key?: string;
    qq?: string;
    operation_type?: string;
    change_content?: string;
    exclude_keyword?: string;
  }) => {
    const api = await getApi();
    return api.get('/record-logs/api/stats', { params });
  },
};

const fieldConfigAPI = {
  getFieldConfigs: async () => {
    const api = await getApi();
    return api.get('/config/fields');
  },
  createFieldConfig: async (data: {
    field_key: string;
    display_name: string;
    field_type?: string;
    dict_key?: string;
    is_visible?: boolean;
    is_batch_editable?: boolean;
    use_default?: boolean;
    default_value?: string;
    column_width?: number;
    sort_order?: number;
    show_in_permission?: boolean;
  }) => {
    const api = await getApi();
    return api.post('/config/fields', data);
  },
  updateFieldConfig: async (id: number, data: {
    field_key: string;
    display_name: string;
    field_type?: string;
    dict_key?: string;
    is_visible?: boolean;
    is_batch_editable?: boolean;
    use_default?: boolean;
    default_value?: string;
    column_width?: number;
    sort_order?: number;
    show_in_permission?: boolean;
  }) => {
    const api = await getApi();
    return api.put(`/config/fields/${id}`, data);
  },
  deleteFieldConfig: async (id: number) => {
    const api = await getApi();
    return api.delete(`/config/fields/${id}`);
  },
  updateSortOrder: async (items: { id: number; sort_order: number }[]) => {
    const api = await getApi();
    return api.put('/config/fields/sort', { items });
  },
  resetDefaults: async () => {
    const api = await getApi();
    return api.post('/config/fields/reset-defaults');
  },
};

const basicConfigAPI = {
  getBasicConfigs: async () => {
    const api = await getApi();
    return api.get('/basic-config');
  },
  createBasicConfig: async (data: {
    field_key: string;
    display_name: string;
    field_type?: string;
    default_value?: string;
    is_return?: boolean;
    is_hidden?: boolean;
    return_in_activate?: boolean;
    return_in_rebind?: boolean;
    return_in_verify?: boolean;
    return_in_permission?: boolean;
    sort_order?: number;
  }) => {
    const api = await getApi();
    return api.post('/basic-config', data);
  },
  updateBasicConfig: async (id: number, data: {
    field_key: string;
    display_name: string;
    field_type?: string;
    default_value?: string;
    is_return?: boolean;
    is_hidden?: boolean;
    return_in_activate?: boolean;
    return_in_rebind?: boolean;
    return_in_verify?: boolean;
    return_in_permission?: boolean;
    sort_order?: number;
  }) => {
    const api = await getApi();
    return api.put(`/basic-config/${id}`, data);
  },
  deleteBasicConfig: async (id: number) => {
    const api = await getApi();
    return api.delete(`/basic-config/${id}`);
  },
};

const systemConfigAPI = {
  getSystemConfig: async () => {
    const api = await getApi();
    return api.get('/config');
  },
  getApiLogging: async () => {
    const api = await getApi();
    return api.get('/config/api-logging');
  },
  setApiLogging: async (enabled: boolean) => {
    const api = await getApi();
    return api.put('/config/api-logging', { enabled });
  },
  getDeviceVerification: async () => {
    const api = await getApi();
    return api.get('/config/device-verification');
  },
  setDeviceVerification: async (enabled: boolean) => {
    const api = await getApi();
    return api.put('/config/device-verification', { enabled });
  },
  getOnlineTimeout: async () => {
    const api = await getApi();
    return api.get('/config/online-timeout');
  },
  setOnlineTimeout: async (timeout_ms: number) => {
    const api = await getApi();
    return api.put('/config/online-timeout', { timeout_ms });
  },
  getRequestSkew: async () => {
    const api = await getApi();
    return api.get('/config/request-skew');
  },
  setRequestSkew: async (skew_ms: number) => {
    const api = await getApi();
    return api.put('/config/request-skew', { skew_ms });
  },
  getRecordCleanupStats: async () => {
    const api = await getApi();
    return api.get('/config/record-cleanup/stats');
  },
  setRecordCleanupConfig: async (cleanup_days: number) => {
    const api = await getApi();
    return api.put('/config/record-cleanup/config', { cleanup_days });
  },
};

const api: Record<string, any> = {
  get: async (url: string, config?: any) => {
    const instance = await getApi();
    return instance.get(url, config);
  },
  put: async (url: string, data?: any, config?: any) => {
    const instance = await getApi();
    return instance.put(url, data, config);
  },
  post: async (url: string, data?: any, config?: any) => {
    const instance = await getApi();
    return instance.post(url, data, config);
  },
  delete: async (url: string, config?: any) => {
    const instance = await getApi();
    return instance.delete(url, config);
  },
};

const wechatGameAPI = {
  list: async () => {
    const api = await getApi();
    return api.get('/wechat-games');
  },
  create: async (data: any) => {
    const api = await getApi();
    return api.post('/wechat-games', data);
  },
  update: async (id: number, data: any) => {
    const api = await getApi();
    return api.put(`/wechat-games/${id}`, data);
  },
  delete: async (id: number) => {
    const api = await getApi();
    return api.delete(`/wechat-games/${id}`);
  },
};

const iosopAPI = {
  login: async (token: string, url: string) => {
    const api = await getApi();
    return api.post('/ios-op/login', { token, url });
  },

  getCategories: async (token: string) => {
    const api = await getApi();
    return api.get('/ios-op/categories', { params: { token } });
  },

  fetchUnused: async (token: string, categoryId: number) => {
    const api = await getApi();
    return api.post('/ios-op/fetch-unused', { token, category_id: categoryId });
  },

  getPricing: async (token: string) => {
    const api = await getApi();
    return api.get('/ios-op/pricing', { params: { token } });
  },
};

const opGameBlockAPI = {
  getList: async (params: { page?: number; limit?: number; search?: string } = {}) => {
    const api = await getApi();
    return api.get('/op-game-block', { params });
  },
  exportList: async (params: { status?: 'all' | 'success' | 'fail'; search?: string } = {}) => {
    const api = await getApi();
    return api.get('/op-game-block/export', { params });
  },
  batchAdd: async (data: string) => {
    const api = await getApi();
    return api.post('/op-game-block/batch', { data });
  },
  deleteItem: async (id: number) => {
    const api = await getApi();
    return api.delete(`/op-game-block/${id}`);
  },
  batchDelete: async (ids: number[]) => {
    const api = await getApi();
    return api.delete('/op-game-block/batch', { data: { ids } });
  },
  deleteDuplicates: async () => {
    const api = await getApi();
    return api.delete('/op-game-block/duplicates');
  },
  deleteAll: async () => {
    const api = await getApi();
    return api.delete('/op-game-block/all');
  },
  getGames: async () => {
    const api = await getApi();
    return api.get('/op-game-block/games');
  },
  getPricing: async () => {
    const api = await getApi();
    return api.get('/op-game-block/pricing');
  },
  blockGames: async (gameId: string, ids: number[], all: boolean = false, concurrency: number = 20) => {
    const api = await getApi();
    return api.post('/op-game-block/block', { game_id: gameId, ids, all, concurrency });
  },
  unblockGames: async (gameId: string, ids: number[], all: boolean = false, concurrency: number = 20) => {
    const api = await getApi();
    return api.post('/op-game-block/unblock', { game_id: gameId, ids, all, concurrency });
  },
  queryBlock: async (ids: number[], all: boolean = false, concurrency: number = 20) => {
    const api = await getApi();
    return api.post('/op-game-block/query-block', { ids, all, concurrency });
  },
  blockConsume: async (ids: number[], all: boolean = false, concurrency: number = 20) => {
    const api = await getApi();
    return api.post('/op-game-block/block-consume', { ids, all, concurrency });
  },
  unblockConsume: async (ids: number[], all: boolean = false, concurrency: number = 20) => {
    const api = await getApi();
    return api.post('/op-game-block/unblock-consume', { ids, all, concurrency });
  },
  timeLimit: async (duration: number, ids: number[], all: boolean = false, concurrency: number = 20) => {
    const api = await getApi();
    return api.post('/op-game-block/time-limit', { duration, ids, all, concurrency });
  },
  getTaskStatus: async (taskId: string) => {
    const api = await getApi();
    return api.get(`/op-game-block/task/${taskId}`);
  },
};

const wxPathAPI = {
  convert: async (links: string) => {
    const api = await getApi();
    return api.post('/wx/path/convert', { links });
  },
  getPrice: async () => {
    const api = await getApi();
    return api.get('/wx/path/price');
  },
};

const wxCodeAPI = {
  getPrice: async () => {
    const api = await getApi();
    return api.get('/wx/code/price');
  },
};

const qrcodeAPI = {
  decode: async (img: string) => {
    const api = await getApi();
    return api.post('/qrcode/decode', { img });
  },
  getPrice: async () => {
    const api = await getApi();
    return api.get('/qrcode/price');
  },
};

const homeContentAPI = {
  getTimeline: async () => {
    return axios.get('/api/home-content/timeline');
  },
  getTimelineAdmin: async () => {
    const api = await getApi();
    return api.get('/admin/homepage-config/timeline');
  },
  updateTimeline: async (value: string) => {
    const api = await getApi();
    return api.put('/admin/homepage-config/timeline', { value });
  },
};

const blogAPI = {
  getPublicBlogs: async () => {
    return axios.get('/api/blogs');
  },
  getPublicBlog: async (id: number) => {
    return axios.get(`/api/blogs/${id}`);
  },
  getAdminBlogs: async () => {
    const api = await getApi();
    return api.get('/admin/blogs');
  },
  getAdminBlog: async (id: number) => {
    const api = await getApi();
    return api.get(`/admin/blogs/${id}`);
  },
  createBlog: async (data: { title: string; summary?: string; content: string; cover_image?: string; is_published?: boolean }) => {
    const api = await getApi();
    return api.post('/admin/blogs', data);
  },
  updateBlog: async (id: number, data: { title?: string; summary?: string; content?: string; cover_image?: string; is_published?: boolean }) => {
    const api = await getApi();
    return api.put(`/admin/blogs/${id}`, data);
  },
  deleteBlog: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/blogs/${id}`);
  },
  uploadImage: async (file: File, onProgress?: (percent: number) => void) => {
    const api = await getApi();
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/blogs/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: any) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
  },
};

const cloudDiskAPI = {
  getPublicConfig: async () => {
    return axios.get('/api/cloud-disk/config');
  },
  upload: async (file: File, options?: { extractionCode?: string; maxDownloads?: number }, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.extractionCode) {
      formData.append('extraction_code', options.extractionCode);
    }
    if (options?.maxDownloads !== undefined && options.maxDownloads > 0) {
      formData.append('max_downloads', String(options.maxDownloads));
    }
    return axios.post('/api/cloud-disk/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
  },
  getShareInfo: async (code: string) => {
    return axios.get(`/api/cloud-disk/share/${code}`);
  },
  verifyCode: async (code: string, extractionCode: string) => {
    return axios.post(`/api/cloud-disk/share/${code}/verify`, { extraction_code: extractionCode });
  },
  getDownloadUrl: (code: string, extractionCode?: string) => {
    const base = `/api/cloud-disk/share/${code}/download`;
    return extractionCode ? `${base}?code=${encodeURIComponent(extractionCode)}` : base;
  },
  getAdminConfig: async () => {
    const api = await getApi();
    return api.get('/admin/cloud-disk/config');
  },
  updateAdminConfig: async (data: { max_file_size_mb: number; save_days: number }) => {
    const api = await getApi();
    return api.put('/admin/cloud-disk/config', data);
  },
  listShares: async (page = 1, limit = 20) => {
    const api = await getApi();
    return api.get('/admin/cloud-disk/shares', { params: { page, limit } });
  },
  deleteShare: async (id: number) => {
    const api = await getApi();
    return api.delete(`/admin/cloud-disk/shares/${id}`);
  },
};

export { authAPI, dataAPI, categoryAPI, externalAPI, adminAPI, clusterAPI, renewalAPI, rechargeAPI, inviteAPI, withdrawAPI, balanceAPI, wechatAuthAPI, cardAPI, cardBindAPI, dictionaryAPI, recordLogAPI, fieldConfigAPI, basicConfigAPI, systemConfigAPI, wechatGameAPI, iosopAPI, opGameBlockAPI, wxPathAPI, wxCodeAPI, qrcodeAPI, homeContentAPI, blogAPI, cloudDiskAPI, kmInterfaceAPI, getApi, getConfig, api };

export const resolveAvatarUrl = (avatarUrl?: string): string | undefined => {
  if (!avatarUrl) return undefined;
  if (avatarUrl.startsWith('/api/')) {
    // Use relative path, browser will use current origin
    return avatarUrl;
  }
  return avatarUrl;
};

export default {
  auth: authAPI,
  data: dataAPI,
  category: categoryAPI,
  external: externalAPI,
  admin: adminAPI,
  withdraw: withdrawAPI,
  wechatAuth: wechatAuthAPI,
  card: cardAPI,
  dictionary: dictionaryAPI,
  recordLog: recordLogAPI,
  fieldConfig: fieldConfigAPI,
  basicConfig: basicConfigAPI,
  systemConfig: systemConfigAPI,
  wechatGame: wechatGameAPI,
  api,
};
